'use client'

import * as React from 'react'
import PhoneInputBase, { getCountries, getCountryCallingCode, type Country } from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
// The package's Ukrainian locale file is named `ua.json`, not `uk.json`.
import ua from 'react-phone-number-input/locale/ua.json'
import en from 'react-phone-number-input/locale/en.json'
import { ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { inputVariants } from '@/components/ui/Input'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { useDefaultCountry } from '@/lib/hooks/useDefaultCountry'
import { DEFAULT_COUNTRY, type CountryCode } from '@/lib/utils/phone'

export interface PhoneInputProps {
  value: string
  onChange: (e164: string) => void
  label?: string
  error?: string
  hint?: string
  required?: boolean
  disabled?: boolean
  inputSize?: 'default' | 'sm' | 'lg'
  placeholder?: string
  id?: string
  'aria-label'?: string
  className?: string
  containerClassName?: string
  defaultCountry?: CountryCode
}

/** Fixed 24×16 flag box; the SVG from `country-flag-icons` has no size of its own. */
const FLAG_BOX =
  'block w-6 h-4 shrink-0 rounded-[3px] overflow-hidden bg-gray-200 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)] [&>svg]:block [&>svg]:w-full [&>svg]:h-full'

/** Search row + list max height, used to decide whether the popup fits below the input. */
const POPUP_HEIGHT = 360

function CountrySelect({
  value,
  onChange,
  labels,
  searchLabels,
  preferred,
  disabled,
}: {
  value?: Country
  onChange: (c?: Country) => void
  labels: Record<string, string>
  /** Country names to also match against while searching (kept in Latin script so a Latin query still finds a country whose displayed `labels` name is in Cyrillic). */
  searchLabels?: Record<string, string>
  /** Countries pinned to the top of the list (deduplicated, in order). */
  preferred?: Country[]
  disabled?: boolean
}) {
  const { t } = useLanguage()
  const [open, setOpen] = React.useState(false)
  const [q, setQ] = React.useState('')
  const [active, setActive] = React.useState(0)
  const [flip, setFlip] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  const listId = React.useId()

  const preferredKey = (preferred ?? []).join(',')
  const countries = React.useMemo(() => {
    const all = getCountries()
      .map(c => ({
        code: c,
        name: labels[c] ?? c,
        searchName: searchLabels?.[c] ?? labels[c] ?? c,
        dial: `+${getCountryCallingCode(c)}`,
        pinned: false,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
    const pins = Array.from(new Set(preferredKey.split(',').filter(Boolean) as Country[]))
      .map(code => all.find(c => c.code === code))
      .filter((c): c is NonNullable<typeof c> => !!c)
      .map(c => ({ ...c, pinned: true }))
    return [...pins, ...all]
  }, [labels, searchLabels, preferredKey])
  const needle = q.trim().toLowerCase()
  const digits = needle.replace(/^\+/, '')
  const visible = React.useMemo(
    () =>
      needle
        ? countries.filter(
            c =>
              !c.pinned &&
              (c.name.toLowerCase().includes(needle) ||
              c.searchName.toLowerCase().includes(needle) ||
                (digits && /^\d+$/.test(digits) && c.dial.slice(1).startsWith(digits)) ||
                c.code.toLowerCase() === needle)
          )
        : countries,
    [countries, needle, digits]
  )

  // Close on outside click; reset the search when closing.
  React.useEffect(() => {
    if (!open) {
      setQ('')
      return
    }
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
  }, [open])

  // Highlight the current country when opening, the first match while searching.
  React.useEffect(() => {
    if (!open) return
    const idx = needle ? 0 : Math.max(0, visible.findIndex(c => c.code === value))
    setActive(idx)
  }, [open, needle, visible, value])

  // Keep the highlighted option in view.
  React.useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [open, active])

  // Open below the input, or above it when the viewport has no room below.
  const toggle = React.useCallback((force?: boolean) => {
    setOpen(prev => {
      const next = force ?? !prev
      if (next && ref.current && typeof window !== 'undefined') {
        const r = ref.current.getBoundingClientRect()
        setFlip(window.innerHeight - r.bottom < POPUP_HEIGHT && r.top > window.innerHeight - r.bottom)
      }
      return next
    })
  }, [])

  const close = React.useCallback((refocus = false) => {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }, [])
  const select = React.useCallback(
    (c: Country) => {
      onChange(c)
      setOpen(false)
    },
    [onChange]
  )

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive(i => Math.min(visible.length - 1, i + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive(i => Math.max(0, i - 1))
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        setActive(Math.max(0, visible.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (visible[active]) select(visible[active].code)
        break
      case 'Escape':
        e.preventDefault()
        close(true)
        break
      case 'Tab':
        close()
        break
    }
  }

  const Flag = value ? flags[value] : null
  const activeId = visible[active] ? `${listId}-${visible[active].pinned ? 'pin-' : ''}${visible[active].code}` : undefined

  return (
    <div ref={ref} className="relative flex shrink-0 self-stretch">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={t('phone.selectCountry')}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => toggle()}
        onKeyDown={e => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            toggle(true)
          }
        }}
        className="flex items-center gap-1.5 pl-3 pr-2 rounded-l-lg border-r border-gray-200 text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-gray-50 disabled:cursor-not-allowed"
      >
        <span className={FLAG_BOX} aria-hidden="true">
          {Flag ? <Flag title={labels[value!] ?? value} /> : null}
        </span>
        <ChevronDown className={cn('h-4 w-4 text-gray-500 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div
          className={cn(
            'absolute left-0 z-50 w-[min(20rem,calc(100vw-2rem))] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden',
            flip ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          <div className="p-2 border-b border-gray-100 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              role="searchbox"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              aria-controls={listId}
              aria-activedescendant={activeId}
              aria-label={t('phone.searchCountry')}
              placeholder={t('phone.searchCountry')}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div ref={listRef} id={listId} role="listbox" className="max-h-72 overflow-y-auto overscroll-contain py-1">
            {visible.length === 0 && <p className="px-3 py-6 text-sm text-center text-gray-500">{t('phone.noCountryFound')}</p>}
            {visible.map((c, i) => {
              const F = flags[c.code]
              const selected = c.code === value
              return (
                <button
                  key={`${c.pinned ? 'pin-' : ''}${c.code}`}
                  id={`${listId}-${c.pinned ? 'pin-' : ''}${c.code}`}
                  data-index={i}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  tabIndex={-1}
                  onMouseMove={() => i !== active && setActive(i)}
                  onClick={() => select(c.code)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 text-sm text-left',
                    // divider under the last pinned country
                    c.pinned && !visible[i + 1]?.pinned && 'border-b border-gray-100 mb-1',
                    i === active ? 'bg-blue-50' : '',
                    selected ? 'text-blue-700 font-medium' : 'text-gray-900'
                  )}
                >
                  <span className={FLAG_BOX} aria-hidden="true">
                    {F ? <F title={c.name} /> : null}
                  </span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-gray-500 tabular-nums">{c.dial}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export function PhoneInput({
  value,
  onChange,
  label,
  error,
  hint,
  required,
  disabled,
  inputSize,
  placeholder,
  id,
  className,
  containerClassName,
  defaultCountry,
  ...rest
}: PhoneInputProps) {
  const { language } = useLanguage()
  const geoCountry = useDefaultCountry()
  const generatedId = React.useId()
  const inputId = id || generatedId
  const errorId = error ? `${inputId}-error` : undefined
  const hintId = hint ? `${inputId}-hint` : undefined
  const labels = (language === 'uk' ? ua : en) as Record<string, string>

  return (
    <div className={cn('w-full', containerClassName)}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <PhoneInputBase
        id={inputId}
        value={value || undefined}
        onChange={v => onChange(v ?? '')}
        international
        countryCallingCodeEditable={false}
        defaultCountry={defaultCountry ?? geoCountry}
        labels={labels}
        flags={flags}
        countrySelectComponent={CountrySelect}
        // `react-phone-number-input` does not forward its own `labels` prop
        // to a custom `countrySelectComponent` (it only uses it internally
        // to build the country names in `options`), so pass it through
        // explicitly via `countrySelectProps`. English names are always
        // included as a search key too, so a Latin-script query still finds
        // a country whose displayed (Ukrainian) name is in Cyrillic.
        countrySelectProps={{ labels, searchLabels: en as Record<string, string>, preferred: [defaultCountry ?? geoCountry, DEFAULT_COUNTRY] }}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={rest['aria-label']}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={[errorId, hintId].filter(Boolean).join(' ') || undefined}
        // No `overflow-hidden` here: the country list is absolutely positioned
        // inside this box and would be clipped to the input's height.
        className={cn(inputVariants({ variant: error ? 'error' : 'default', inputSize }), 'flex items-stretch p-0 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent', className)}
        numberInputProps={{ className: 'flex-1 min-w-0 h-full px-3 bg-transparent outline-none text-sm rounded-r-lg', required }}
      />
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="mt-1 text-sm text-gray-500">
          {hint}
        </p>
      )}
    </div>
  )
}
